import { FC } from 'react'
import LazyHydrate from 'react-lazy-hydration'
import { Button, LinkButton } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { ExternalLinkIcon } from '@/atoms/Icons/ExternalLinkIcon'
import { Image } from '@/atoms/Image'
import { ModalSheet } from '@/atoms/ModalSheet'
import { CaptionLG, HeadingXS } from '@/atoms/Text'
import { useSafeAnalytics } from '@/utils/analytics/safe-analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import useWindowSize from '@/utils/useWindowSize'
import { AppDownloadLinks } from '../AppDownloadLinks'
import { useGetAppBanner } from '../GetAppBanner'
import { AppBannerAnalyticsContext } from '../GetAppBanner/types'

interface Props {
  analytics: {
    projectSlug?: string
    context: AppBannerAnalyticsContext
  }
  isOpen: boolean
  onClose: () => void
}

export const AppPromoModal: FC<Props> = ({ analytics, isOpen, onClose }) => {
  const { t } = useTranslate('common')
  const { deepLink } = useGetAppBanner()
  const { track } = useSafeAnalytics()
  const { innerHeight } = useWindowSize()

  if (!deepLink) return null

  return (
    <LazyHydrate whenVisible>
      <ModalSheet isOpen={isOpen} onClose={onClose} sheetHeight="auto">
        <Button
          variant="plain"
          onClick={() => {
            onClose()
            track('Get App Banner Dismissed', analytics)
          }}
          className="absolute right-4 top-4 size-8 rounded-full bg-core-gray-100 p-2"
        >
          <CloseIcon color="black" size={16} />
        </Button>
        <div className="overflow-auto p-8 text-center text-black se:max-h-[80vh]">
          <Image
            src="v1660765800/angel-app/Branch.io/Deepview%20App%20Icons/Angel-App-Icon"
            alt="Angel App Icon"
            width={64}
            height={64}
            className="mx-auto mb-3 rounded-lg"
          />
          <HeadingXS className="mb-6" weight="bold">
            {t('watchWithTheBestExperience', 'Watch with the Best Experience')}
          </HeadingXS>
          <CaptionLG className="se:photon-caption-md">
            {t(
              'downloadTheAngelApp',
              'Download the Angel App for smoother playback, better audio, and bigger screens.',
            )}
          </CaptionLG>

          <CaptionLG className="my-6" weight="bold">
            {t('alsoAvailableOnSmartTvs', 'Also available on smart TVs')}
          </CaptionLG>
          <AppDownloadLinks layout={innerHeight > 768 ? 'loose' : 'tight'} />

          <LinkButton
            className="mt-8"
            href={deepLink}
            onClick={() => {
              track('Get App Clicked', { location: 'GetAppBanner', ...analytics })
              track('Get App Banner Button Clicked', analytics)
            }}
            variant="black"
            external
          >
            {t('continueToTheAngelApp', 'Continue to the Angel App')}
            <ExternalLinkIcon color="white" className="ml-1.5" size={24} />
          </LinkButton>
        </div>
      </ModalSheet>
    </LazyHydrate>
  )
}
